import type { BatMetadataConfigEntry } from './common/types';
import { coreFields } from './common/coreFields';
import {
  countUnitField,
  factoryIdField,
  factoryCountField,
} from './common/sharedFields';
import { locationFields } from './common/locationFields';
import { vendorFields } from './common/vendorFields';
import { productFields } from './common/productFields';
import { purchasedMaterialFields } from './common/productFields';
import { allCleanPowerFields } from './common/cleanPowerFields';
import { msg } from '@lingui/core/macro';
import { wasteQuantityField } from './common/wasteFields';

export const tier1FactoriesActivityDataMetadata = {
  displayName: msg({
    message: 'Supplier factories energy, waste, and water: Tier 1',
    context:
      'Activity data schema name for supplier factories energy, waste, and water: Tier 1',
  }),
  fields: {
    ...coreFields,
    ...locationFields,
    ...vendorFields,
    ...productFields,
    ...purchasedMaterialFields,
    ...allCleanPowerFields,
    biodiesel_fuel_mj: {
      displayName: msg({
        message: 'Biodiesel fuel consumption',
        context: 'Energy from biodiesel.',
      }),
      externalDescription: msg({
        message:
          "The amount of biodiesel consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'Biodiesel fuel',
      }),
    },
    biomass_non_wood_mj: {
      displayName: msg({
        message: 'Biomass (non-wood) consumption',
        context: 'Energy from biomass (non-wood).',
      }),
      externalDescription: msg({
        message:
          "The amount of biomass (non-wood) consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'Biomass (non-wood) quantity',
      }),
    },
    biomass_wood_mj: {
      displayName: msg({
        message: 'Biomass (wood) consumption',
        context: 'Energy from biomass (wood).',
      }),
      externalDescription: msg({
        message:
          "The amount of biomass (wood) consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'Biomass (wood) quantity',
      }),
    },
    coal_mj: {
      displayName: msg({
        message: 'Coal consumption',
        context: 'Energy from coal.',
      }),
      externalDescription: msg({
        message:
          "The amount of coal consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'Coal quantity',
      }),
    },
    diesel_fuel_mj: {
      displayName: msg({
        message: 'Diesel fuel consumption',
        context: 'Energy from diesel fuel.',
      }),
      externalDescription: msg({
        message:
          "The amount of diesel fuel consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'Diesel fuel quantity',
      }),
    },
    district_heat_mj: {
      displayName: msg({
        message: 'District heat consumption',
        context: 'Energy from district heat.',
      }),
      externalDescription: msg({
        message:
          "The amount of district heat consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'District heat quantity',
      }),
    },
    electricity_mj: {
      displayName: msg({
        message: 'Electricity consumption',
        context: 'Energy from electricity.',
      }),
      externalDescription: msg({
        message:
          "The amount of electricity consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'Electricity quantity',
      }),
    },
    factory_id: factoryIdField,
    fuel_oil_mj: {
      displayName: msg({
        message: 'Fuel oil consumption',
        context: 'Energy from fuel oil.',
      }),
      externalDescription: msg({
        message:
          "The amount of fuel oil consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'Fuel oil quantity',
      }),
    },
    gasoline_mj: {
      displayName: msg({
        message: 'Gasoline consumption',
        context: 'Energy from gasoline.',
      }),
      externalDescription: msg({
        message:
          "The amount of gasoline consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'Gasoline quantity',
      }),
    },
    liquified_natural_gas_mj: {
      displayName: msg({
        message: 'LNG quantity',
        context: 'Energy from liquified natural gas.',
      }),
      externalDescription: msg({
        message:
          "The amount of liquefied natural gas consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'LNG quantity',
      }),
    },
    liquified_petroleum_gas_mj: {
      displayName: msg({
        message: 'Liquefied petroleum gas consumption',
        context: 'Energy from liquified petroleum gas.',
      }),
      externalDescription: msg({
        message:
          "The amount of liquefied petroleum gas consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'LNG quantity',
      }),
    },
    natural_gas_mj: {
      displayName: msg({
        message: 'Natural gas consumption',
        context: 'Energy from natural gas.',
      }),
      externalDescription: msg({
        message:
          "The amount of natural gas consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'Natural gas quantity',
      }),
    },
    order_quantity: factoryCountField,
    // NOTE: quantity does not exist on the BAT but order_quantity is
    // renamed to quantity in the BART-EM matching process
    quantity: factoryCountField,
    order_unit: countUnitField,
    propane_mj: {
      displayName: msg({
        message: 'Propane consumption',
        context: 'Energy from propane.',
      }),
      externalDescription: msg({
        message:
          "The amount of propane consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'Propane quantity',
      }),
    },
    steam_mj: {
      displayName: msg({
        message: 'Steam consumption',
        context: 'Energy from steam.',
      }),
      externalDescription: msg({
        message:
          "The amount of steam consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'Steam quantity',
      }),
    },
    waste_kg: {
      ...wasteQuantityField,
      externalDescription: msg({
        message:
          "The amount of waste produced at the tier 1 factory, specifically for the reporting company's products.",
        context: 'Quantity of waste in kilograms',
      }),
      displayName: msg({ message: 'Waste production' }),
    },
    water_liters: {
      displayName: msg({
        message: 'Water consumption',
        context: 'Volume of water.',
      }),
      externalDescription: msg({
        message:
          "The amount of water consumed at the tier 1 factory, specifically for the reporting company's products.",
        context: 'Water quantity',
      }),
    },
  },
} as const satisfies BatMetadataConfigEntry;
